import React from 'react'
import { Input, Btn } from '../ui/index.jsx'
import { useLanguage } from '../../i18n/index.jsx'
import { PAPER } from '../../lib/printColors.js'

type MfaStep = 'idle' | 'setup' | 'recovery' | 'disable'

interface MfaSetupProps {
  mfaEnabled: boolean
  mfaLoading: boolean
  mfaStep: MfaStep
  mfaQr: string | null
  mfaSecret: string | null
  mfaCode: string
  setMfaCode: (code: string) => void
  mfaRecovery: string[]
  mfaError: string | null
  mfaDisablePw: string
  setMfaDisablePw: (pw: string) => void
  onSetup: () => void
  onVerifySetup: React.FormEventHandler<HTMLFormElement>
  onDisable: React.FormEventHandler<HTMLFormElement>
  onSetStep: (step: MfaStep) => void
  onClearError: () => void
}

export default function MfaSetup({
  mfaEnabled, mfaLoading, mfaStep, mfaQr, mfaSecret,
  mfaCode, setMfaCode, mfaRecovery, mfaError,
  mfaDisablePw, setMfaDisablePw,
  onSetup, onVerifySetup, onDisable, onSetStep, onClearError,
}: MfaSetupProps) {
  const { t } = useLanguage()

  return (
    <div style={{ paddingBottom: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
      <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: '1rem' }}>{t('twoFactorAuth')}</h3>

      {mfaError && (
        <div style={{
          background: 'var(--danger-dim)', border: '1px solid rgba(248,113,113,0.2)',
          color: 'var(--danger)', padding: '10px 12px', borderRadius: 'var(--radius)',
          fontSize: 'var(--text-caption)', marginBottom: '0.75rem'
        }}>
          {mfaError}
        </div>
      )}

      {/* Idle state -- show enable or disable button */}
      {mfaStep === 'idle' && !mfaEnabled && (
        <Btn variant="primary" size="sm" onClick={onSetup} loading={mfaLoading}>
          {t('enable2FA')}
        </Btn>
      )}

      {mfaStep === 'idle' && mfaEnabled && (
        <div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--success)', marginBottom: '0.75rem' }}>
            {t('mfaEnabled')}
          </p>
          <Btn variant="ghost" size="sm" onClick={() => onSetStep('disable')}>
            {t('disable2FA')}
          </Btn>
        </div>
      )}

      {/* Setup: show QR code and verify */}
      {mfaStep === 'setup' && (
        <form onSubmit={onVerifySetup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{t('scanQRCode')}</p>
          {mfaQr && (
            <div style={{ textAlign: 'center' }}>
              <img src={mfaQr} alt="QR Code" loading="lazy" style={{ width: 180, height: 180, borderRadius: 'var(--radius)', background: PAPER, padding: 8 }} />
            </div>
          )}
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', wordBreak: 'break-all', textAlign: 'center', fontFamily: 'monospace' }}>
            {mfaSecret}
          </div>
          <Input
            label={t('enterVerificationCode')}
            type="text"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value)}
            placeholder="000000"
            autoComplete="one-time-code"
            maxLength={6}
          />
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" size="sm" type="button" onClick={() => { onSetStep('idle'); onClearError() }}>
              {t('cancel')}
            </Btn>
            <Btn variant="primary" size="sm" type="submit" loading={mfaLoading} disabled={!mfaCode || mfaCode.length < 6}>
              {t('verifyAndEnable')}
            </Btn>
          </div>
        </form>
      )}

      {/* Recovery codes display (shown once after enabling) */}
      {mfaStep === 'recovery' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ fontSize: 'var(--text-base)', fontWeight: 600 }}>{t('recoveryCodesTitle')}</p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{t('recoveryCodesDesc')}</p>
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius)', padding: '12px 16px',
            fontFamily: 'monospace', fontSize: 'var(--text-caption)', lineHeight: 1.8,
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px',
          }}>
            {mfaRecovery.map((code, i) => <span key={i}>{code}</span>)}
          </div>
          <Btn variant="primary" size="sm" onClick={() => onSetStep('idle')}>
            {t('done')}
          </Btn>
        </div>
      )}

      {/* Disable confirmation */}
      {mfaStep === 'disable' && (
        <form onSubmit={onDisable} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{t('enterPasswordToDisable')}</p>
          <Input
            label={t('passwordLabel')}
            type="password"
            value={mfaDisablePw}
            onChange={e => setMfaDisablePw(e.target.value)}
            required
          />
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" size="sm" type="button" onClick={() => { onSetStep('idle'); onClearError(); setMfaDisablePw('') }}>
              {t('cancel')}
            </Btn>
            <Btn variant="danger" size="sm" type="submit" loading={mfaLoading} disabled={!mfaDisablePw}>
              {t('disable2FA')}
            </Btn>
          </div>
        </form>
      )}
    </div>
  )
}
